import { useEffect, useState } from "react";
import { Plus, Pencil, Briefcase, Globe, EyeOff, XCircle } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { adminApi, formatApiErrorDetail } from "../lib/adminApi";

const EMPTY = {
  title: "", department: "", location: "", employmentType: "Full-time", experience: "",
  skills: "", description: "", responsibilities: "", qualifications: "", deadline: "",
};

const STATUS_STYLES = {
  draft: "bg-neutral-100 text-neutral-500",
  published: "bg-brand-red text-white",
  closed: "bg-brand-graphite text-white",
};

const JobForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const payload = { ...form, skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      const res = form.id
        ? await adminApi.put(`/admin/jobs/${form.id}`, payload)
        : await adminApi.post("/admin/jobs", payload);
      onSaved(res.data, !form.id);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="border border-brand-red/30 bg-white p-6 sm:p-8" data-testid="admin-job-form">
      <h2 className="text-lg font-semibold text-brand-graphite">
        {form.id ? "Edit Job Opening" : "New Job Opening"}
      </h2>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label className="field-label">Job Title *</label>
          <input className="field-input" required value={form.title} onChange={set("title")} data-testid="job-form-title" />
        </div>
        <div>
          <label className="field-label">Department *</label>
          <input className="field-input" required value={form.department} onChange={set("department")} placeholder="e.g. Engineering" data-testid="job-form-department" />
        </div>
        <div>
          <label className="field-label">Location / Work Arrangement *</label>
          <input className="field-input" required value={form.location} onChange={set("location")} placeholder="e.g. Hyderabad, India (Hybrid)" data-testid="job-form-location" />
        </div>
        <div>
          <label className="field-label">Employment Type *</label>
          <select className="field-input" value={form.employmentType} onChange={set("employmentType")} data-testid="job-form-employment-type">
            {["Full-time", "Part-time", "Contract", "Internship"].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label">Experience Required *</label>
          <input className="field-input" required value={form.experience} onChange={set("experience")} placeholder="e.g. 3–5 years" data-testid="job-form-experience" />
        </div>
        <div>
          <label className="field-label">Application Deadline *</label>
          <input type="date" className="field-input" required value={form.deadline} onChange={set("deadline")} data-testid="job-form-deadline" />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Skills (comma-separated) *</label>
          <input className="field-input" required value={form.skills} onChange={set("skills")} placeholder="e.g. Python, FastAPI, React, MongoDB" data-testid="job-form-skills" />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Description *</label>
          <textarea rows={4} className="field-input resize-y" required value={form.description} onChange={set("description")} data-testid="job-form-description" />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Responsibilities *</label>
          <textarea rows={4} className="field-input resize-y" required value={form.responsibilities} onChange={set("responsibilities")} placeholder="One per line" data-testid="job-form-responsibilities" />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Qualifications *</label>
          <textarea rows={4} className="field-input resize-y" required value={form.qualifications} onChange={set("qualifications")} placeholder="One per line" data-testid="job-form-qualifications" />
        </div>
      </div>
      {error && (
        <p className="mt-5 border-l-2 border-brand-red bg-brand-surface p-3 text-sm text-brand-graphite" role="alert" data-testid="job-form-error">
          {error}
        </p>
      )}
      <div className="mt-6 flex gap-3">
        <button type="submit" className="btn-primary" disabled={saving} data-testid="job-form-save-button">
          {saving ? "Saving…" : form.id ? "Save Changes" : "Create Opening"}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} data-testid="job-form-cancel-button">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/jobs").then((res) => setJobs(res.data.items)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const onSaved = (job, isNew) => {
    setJobs((prev) => (isNew ? [job, ...prev] : prev.map((j) => (j.id === job.id ? job : j))));
    setEditing(null);
  };

  const setStatus = async (job, status) => {
    const res = await adminApi.patch(`/admin/jobs/${job.id}/status`, { status });
    setJobs((prev) => prev.map((j) => (j.id === job.id ? res.data : j)));
  };

  const startEdit = (job) =>
    setEditing({ ...job, skills: job.skills.join(", ") });

  return (
    <AdminLayout title="Job Openings">
      <div className="flex items-center justify-between">
        <p className="text-sm text-brand-grey">
          Only <strong className="text-brand-graphite">published</strong> openings appear on the public Careers page.
        </p>
        {!editing && (
          <button type="button" className="btn-primary flex items-center gap-2" onClick={() => setEditing(EMPTY)} data-testid="admin-new-job-button">
            <Plus className="h-4 w-4" aria-hidden="true" /> New Opening
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-6">
          <JobForm initial={editing} onSaved={onSaved} onCancel={() => setEditing(null)} />
        </div>
      )}

      <div className="mt-6 space-y-3" data-testid="admin-jobs-list">
        {loading ? (
          <p className="py-12 text-center text-sm text-brand-grey">Loading openings…</p>
        ) : jobs.length === 0 ? (
          <div className="border border-border bg-white p-12 text-center" data-testid="admin-jobs-empty">
            <Briefcase className="mx-auto h-8 w-8 text-brand-grey" aria-hidden="true" />
            <p className="mt-3 text-sm text-brand-grey">No job openings yet. Create your first opening above.</p>
          </div>
        ) : (
          jobs.map((j) => (
            <div key={j.id} className="border border-border bg-white p-4 sm:p-5" data-testid={`admin-job-${j.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-brand-graphite">{j.title}</p>
                    <span className={`px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLES[j.status]}`} data-testid={`admin-job-status-${j.id}`}>
                      {j.status}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-sm text-brand-grey">
                    {j.department} · {j.location} · {j.employmentType} · {j.experience}
                  </p>
                  <p className="mt-0.5 text-xs text-neutral-400">
                    Deadline {j.deadline} · {j.applicationCount} application{j.applicationCount === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" className="btn-secondary flex items-center gap-1.5 px-3 py-2 text-xs" onClick={() => startEdit(j)} data-testid={`admin-job-edit-${j.id}`}>
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Edit
                  </button>
                  {j.status !== "published" && (
                    <button type="button" className="flex items-center gap-1.5 bg-brand-red px-3 py-2 text-xs font-semibold text-white hover:bg-red-700" onClick={() => setStatus(j, "published")} data-testid={`admin-job-publish-${j.id}`}>
                      <Globe className="h-3.5 w-3.5" aria-hidden="true" /> Publish
                    </button>
                  )}
                  {j.status === "published" && (
                    <button type="button" className="btn-secondary flex items-center gap-1.5 px-3 py-2 text-xs" onClick={() => setStatus(j, "draft")} data-testid={`admin-job-unpublish-${j.id}`}>
                      <EyeOff className="h-3.5 w-3.5" aria-hidden="true" /> Unpublish
                    </button>
                  )}
                  {j.status !== "closed" && (
                    <button type="button" className="flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-brand-grey hover:text-brand-graphite" onClick={() => setStatus(j, "closed")} data-testid={`admin-job-close-${j.id}`}>
                      <XCircle className="h-3.5 w-3.5" aria-hidden="true" /> Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
}
